"use client"

import { ProductCategory } from "@medusajs/medusa"
import { Heading } from "@medusajs/ui"
import React, { useState, useEffect, useRef } from "react"

import { buildRoute } from "@lib/routes"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

interface HeroHelperProps {
  categories: ProductCategory[]
  autoScrollDuration?: number
}
const TRANSITION_MS = 500

const HeroHelper = (props: HeroHelperProps) => {
  const { categories, autoScrollDuration = 10000 } = props

  const length = categories?.length || 0
  const slides = length > 1 ? [categories[length - 1], ...categories, categories[0]] : [...categories]

  const initialIndex = length > 1 ? 1 : 0

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [transitionEnabled, setTransitionEnabled] = useState(true)
  const intervalId = useRef<number | null>(null)

  const nextSlide = () => {
    setCurrentIndex((prev) => prev + 1)
  }

  useEffect(() => {
    if (length <= 1) return

    const startInterval = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
      intervalId.current = window.setInterval(() => {
        nextSlide()
      }, autoScrollDuration)
    }

    const stopInterval = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startInterval()
      } else {
        stopInterval()
      }
    }

    if (document.visibilityState === "visible") {
      startInterval()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      stopInterval()
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [length, autoScrollDuration])

  useEffect(() => {
    if (!transitionEnabled) {
      requestAnimationFrame(() => {
        setTransitionEnabled(true)
      })
    }
  }, [transitionEnabled])

  const handleTransitionEnd = () => {
    if (currentIndex === 0) {
      setTransitionEnabled(false)
      setCurrentIndex(length)
    } else if (currentIndex === slides.length - 1) {
      setTransitionEnabled(false)
      setCurrentIndex(1)
    }
  }

  if (length === 0) {
    return null
  }

  const containerStyle = {
    transform: `translateY(-${currentIndex * 100}%)`,
    transition: transitionEnabled ? `transform ${TRANSITION_MS}ms ease-in-out` : "none",
  }

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-[20px]">
      <div className="h-full" style={containerStyle} onTransitionEnd={handleTransitionEnd}>
        {slides.map((category, index) => (
          <LocalizedClientLink
            key={`${category.id}_${category.handle}_${index}`}
            href={buildRoute.category(category.handle)}
            className="group"
          >
            <div className="relative aspect-square w-full shrink-0" style={{ height: "100%" }}>
              <img
                src={category.metadata.hero_image as string}
                alt={`Categoría de productos: ${category.name}`}
                className="block h-full w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 z-10">
                <Heading level="h1" className="m-0 mb-8 text-2xl text-white">
                  <span className="font-manrope font-bold">{category.name}</span>
                  <p className="text-lg text-white">Compra con Nelo</p>
                </Heading>
              </div>
            </div>
          </LocalizedClientLink>
        ))}
      </div>
    </div>
  )
}

export default HeroHelper
